/**
 * API communication service
 * Refactored with DRY pattern using factory functions
 */

import i18n from '../i18n';

// ============ CSRF PROTECTION ============

// Wrap native fetch to auto-add X-Requested-With on state-changing requests
const _originalFetch = window.fetch.bind(window);
window.fetch = function(url, options = {}) {
  if (options.method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method.toUpperCase())) {
    options.headers = {
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers,
    };
  }
  return _originalFetch(url, options);
};

// ============ FACTORY FUNCTIONS ============

/**
 * Creates a simple GET fetcher with error handling and default value
 * @param {string} url - The API endpoint URL
 * @param {any} defaultValue - Default value to return on error
 * @param {Object} options - Fetch options (credentials, etc.)
 * @returns {Function} Async function that fetches the data
 */
const createFetcher = (url, defaultValue, options = {}) => {
  return async () => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      return defaultValue;
    }
  };
};

/**
 * Creates a fetcher that throws on error (for cases where we need error handling upstream)
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Function} Async function that fetches the data or throws
 */
const createThrowingFetcher = (url, options = {}) => {
  return async () => {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }
    return await response.json();
  };
};

// ============ CACHE MANAGEMENT ============

const apiCache = {
  mapData: null,
  sounds: null,
  userSettings: null,
  challenge: null,
  pendingRequests: {}
};

/**
 * Deduplicates concurrent requests to the same endpoint
 */
const deduplicateRequest = async (cacheKey, requestFn) => {
  if (apiCache[cacheKey]) {
    return apiCache[cacheKey];
  }

  if (apiCache.pendingRequests[cacheKey]) {
    return apiCache.pendingRequests[cacheKey];
  }

  apiCache.pendingRequests[cacheKey] = requestFn().then(result => {
    apiCache[cacheKey] = result;
    delete apiCache.pendingRequests[cacheKey];
    return result;
  }).catch(error => {
    delete apiCache.pendingRequests[cacheKey];
    throw error;
  });

  return apiCache.pendingRequests[cacheKey];
};

/**
 * Clear cache for a specific key or all keys
 */
export const clearApiCache = (key = null) => {
  if (key) {
    apiCache[key] = null;
  } else {
    apiCache.mapData = null;
    apiCache.sounds = null;
    apiCache.userSettings = null;
    apiCache.challenge = null;
  }
};

// ============ SIMPLE FETCHERS (using factory) ============

export const fetchMatomoConfig = createFetcher(
  '/api/config/matomo',
  { url: '', siteId: '' }
);

export const fetchAdsConfig = createFetcher(
  '/api/config/ads',
  { apiUrl: '', apiKey: '' }
);

export const fetchCategories = createFetcher(
  '/api/gamemode-categories',
  []
);

// ============ FETCHERS WITH CUSTOM LOGIC ============

export const fetchRandomBackgroundImage = async () => {
  try {
    const response = await fetch('/api/background');
    if (!response.ok) {
      throw new Error('Failed to fetch background image');
    }
    const data = await response.json();
    return data.imagePath;
  } catch (error) {
    console.error('Error fetching background image:', error);
    return '#1a1a2e'; // Default dark blue background
  }
};

export const fetchMapData = () => {
  return deduplicateRequest('mapData', createFetcher('/api/maps', {}));
};

export const fetchCurrentUser = createFetcher(
  '/api/user',
  null,
  { credentials: 'include' }
);

export const fetchUserScores = async () => {
  const fetcher = createThrowingFetcher('/api/user/scores', { credentials: 'include' });
  try {
    return await fetcher();
  } catch (error) {
    console.error('Error fetching user scores:', error);
    throw error;
  }
};

export const fetchUserSettings = () => {
  return deduplicateRequest('userSettings', async () => {
    const fetcher = createFetcher(
      '/api/user/settings',
      { exclude_from_stats: false },
      { credentials: 'include' }
    );
    return fetcher();
  });
};

export const fetchSounds = () => {
  return deduplicateRequest('sounds', createFetcher(
    '/api/sounds',
    [],
    { credentials: 'include' }
  ));
};

// ============ LEADERBOARD FETCHERS ============

export const fetchDailyLeaderboard = async (difficulty = 'hard', category = null, limit = 50, offset = 0, friends = false) => {
  let url = `/api/leaderboard/daily?difficulty=${difficulty}&limit=${limit}&offset=${offset}`;
  if (category) {
    url += `&category=${encodeURIComponent(category)}`;
  }
  if (friends) {
    url += '&friends=true';
  }
  return createFetcher(url, [], { credentials: 'include' })();
};

export const fetchWeeklyLeaderboard = async (difficulty = 'hard', category = null, limit = 50, offset = 0, friends = false) => {
  let url = `/api/leaderboard/weekly?difficulty=${difficulty}&limit=${limit}&offset=${offset}`;
  if (category) {
    url += `&category=${encodeURIComponent(category)}`;
  }
  if (friends) {
    url += '&friends=true';
  }
  return createFetcher(url, [], { credentials: 'include' })();
};

export const fetchPerfectGamesLeaderboard = async (limit = 50, offset = 0) => {
  return createFetcher(`/api/leaderboard/perfect-games?limit=${limit}&offset=${offset}`, [])();
};

export const fetchScoreStats = async () => {
  return createThrowingFetcher('/api/user/score-stats', { credentials: 'include' })();
};

export const fetchDailyDistribution = async (date = null) => {
  const params = date ? `?date=${date}` : '';
  return createFetcher(`/api/challenges/daily/distribution${params}`, { credentials: 'include' })();
};

export const fetchDailyChallengeLeaderboard = async (date = null, limit = 50) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (limit) params.append('limit', limit);
  return createFetcher(`/api/challenges/daily/leaderboard?${params}`)();
};

export const fetchDailyChallengeMeta = async () => {
  return createFetcher('/api/challenges/daily')();
};

// ============ GAME FLOW FUNCTIONS ============

export const fetchNewChallenge = async (difficulty, sessionId = null, gamemode = null, dailyChallengeId = null) => {
  let url = `/api/challenge?difficulty=${difficulty}`;
  if (sessionId) url += `&sessionId=${sessionId}`;
  if (gamemode) url += `&gamemode=${gamemode}`;
  if (dailyChallengeId) url += `&dailyChallengeId=${dailyChallengeId}`;

  const cacheKey = `challenge_${difficulty}_${sessionId || 'new'}_${gamemode || 'default'}`;

  if (apiCache.pendingRequests[cacheKey]) {
    return apiCache.pendingRequests[cacheKey];
  }

  apiCache.pendingRequests[cacheKey] = (async () => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || i18n.t('errors.fetchChallenge', 'Error fetching challenge'));
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      const result = await response.json();
      delete apiCache.pendingRequests[cacheKey];
      return result;
    } catch (error) {
      console.error('Error:', error);
      delete apiCache.pendingRequests[cacheKey];

      if (error.response && error.response.status === 403) {
        throw error;
      }

      return {
        id: 'challenge_' + Math.floor(Math.random() * 1000),
        image: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAMCAgMCAgMDAwMEAwMEBQgFBQQEBQoHBwYIDAoMDAsKCwsNDhIQDQ4RDgsLEBYQERMUFRUVDA8XGBYUGBIUFRT/wAARC...'
      };
    }
  })();

  return apiCache.pendingRequests[cacheKey];
};

export const submitPosition = async (gameId, map, position, sessionId = null, timeRemaining = null) => {
  const data = { gameId, map, position, sessionId, timeRemaining };

  try {
    const response = await fetch('/api/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    if (response.status === 400) {
      const errorData = await response.json();
      if (errorData.error === 'Map mismatch') {
        return {
          score: 0,
          distance: -1,
          actualPosition: { x: 0, y: 0, description: i18n.t('game.wrongMapSelected', 'Wrong map selected') }
        };
      } else if (errorData.error === 'time_expired') {
        return {
          score: 0,
          distance: -1,
          actualPosition: { x: 0, y: 0, description: i18n.t('game.timeExpiredSubmission', 'Time expired. Your submission was not accepted.') },
          timeExpired: true
        };
      }
      throw new Error(i18n.t('errors.submitPosition', 'Error submitting position'));
    }

    if (!response.ok) {
      throw new Error(i18n.t('errors.submitPosition', 'Error submitting position'));
    }

    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    return {
      score: Math.floor(Math.random() * 5000),
      distance: Math.floor(Math.random() * 500),
      actualPosition: {
        x: Math.random() * 1000,
        y: Math.random() * 800,
        description: i18n.t('errors.simulatedPosition', 'Simulated position (error occurred)')
      }
    };
  }
};

export const finalizeGameSession = async (sessionId) => {
  if (!sessionId) {
    console.warn('No session ID found, cannot finalize game session');
    return null;
  }

  try {
    const response = await fetch(`/api/game/finalize/${sessionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to finalize game session');
    }
    return await response.json();
  } catch (error) {
    console.error('Error finalizing game session:', error);
    return null;
  }
};

// ============ USER ACTIONS ============

export const updateUserSettings = async (settings) => {
  try {
    const response = await fetch('/api/user/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to update user settings');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
};

export const uploadAvatar = async (file) => {
  const formData = new FormData();
  formData.append('avatar', file);

  const response = await fetch('/api/user/avatar', {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || i18n.t('errors.uploadAvatar', 'Failed to upload avatar'));
  }

  return response.json();
};

export const deleteAvatar = async () => {
  const response = await fetch('/api/user/avatar', {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || i18n.t('errors.deleteAvatar', 'Failed to delete avatar'));
  }

  return response.json();
};

export const logout = async () => {
  try {
    const response = await fetch('/api/auth/logout', { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to logout');
    }
    window.location.href = '/';
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    return false;
  }
};

// ============ SSO PROFILE ============

export const fetchSsoProfile = createFetcher(
  '/api/user/sso-profile',
  null,
  { credentials: 'include' }
);

export const updateSsoProfile = async (data) => {
  const response = await fetch('/api/user/sso-profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include'
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || i18n.t('errors.updateProfile', 'Failed to update profile'));
  }
  return response.json();
};

// ============ PUBLIC PROFILE ============

/**
 * Fetch public SSO profile by identifier (battlenet_id or battleTag)
 * Uses backend proxy for server-to-server SSO call
 * @param {string} identifier - BattleTag (e.g., Elsa#21790) or BattleNetID (e.g., 101687196)
 * @returns {Promise<Object>} SSO profile data
 */
export const fetchPublicSsoProfile = async (identifier) => {
  const encoded = encodeURIComponent(identifier);
  const response = await fetch(`/api/user/sso-profile/${encoded}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(i18n.t('errors.userNotFound', 'User not found'));
    }
    throw new Error(i18n.t('errors.fetchProfile', 'Failed to fetch profile'));
  }

  return response.json();
};

/**
 * Fetch local public profile (stats and achievements from our database)
 * @param {string} battleTag - BattleTag (e.g., Elsa#21790)
 * @returns {Promise<Object|null>} Local profile data or null if not found
 */
export const fetchLocalPublicProfile = async (battleTag) => {
  const encoded = encodeURIComponent(battleTag);
  const response = await fetch(`/api/user/public/${encoded}`);

  if (!response.ok) {
    if (response.status === 404) {
      return null; // User not found locally, not an error
    }
    throw new Error(i18n.t('errors.fetchLocalProfile', 'Failed to fetch local profile'));
  }

  return response.json();
};
